import React, {useState} from 'react';
import PropTypes from 'prop-types';
import styled, {css} from 'styled-components';
import {
  ThemeSchema,
  focusOutlineColor,
  focusOutlineStyle,
  focusOutlineWidth,
  focusOutlineOffset,
  colorBlack,
  colorWhite,
  colorBrandBase,
  colorBrandLight,
  colorDark,
  colorLightest,
  colorDarkest,
  colorDarker,
  copyFontFamily,
  copyFontSizeSmall,
  copyLineHeight
} from '@nib-components/theme';
import {Box, Inline, Stack, Columns, Column, Hidden, Container, breakpoint, p, pl, pb, px, py, m, mr} from '@nib/layout';
import Link from '@nib-components/link';
import {EmailSystemIcon, LocationSystemIcon, PhoneSystemIcon, ChevronRightSystemIcon, ChevronDownSystemIcon} from '@nib/icons';
import {SmallFooter} from '../SmallFooter';
import Expander from '@nib-components/expander';

import {AcknowledgmentOfCountry} from './AcknowledgementOfCountry';
import configByVariation from '../config';
import {ContactConfig, CustomFooterConfig, validVariationValues, Variation} from '../shared';

const FooterWrapper = styled.footer`
  font-family: ${copyFontFamily};
  font-size: ${copyFontSizeSmall};

  ${({theme}: {theme: ThemeSchema}) =>
    (theme.id === 'nib' || theme.id === 'foundation') &&
    css`
      --text-color: ${colorWhite};
      --background-color: ${colorBrandBase};
      --heading-text-color: ${colorBrandLight};
      --icon-hover-color: ${colorDark};
    `}

  color: var(--text-color, ${colorLightest});
  background-color: var(--background-color, ${colorDarkest});
`;

const FooterContents = styled.div`
  ${p({xs: 4, sm: 5})};
  ${breakpoint('xs', 'md')`
    ${pb(0)};
  `};
`;

const FooterHeading = styled.div`
  font-weight: bold;
  min-height: 1.5rem;
  color: var(--heading-text-color, inherit);
`;

const FooterLinkColumns = styled(Columns)`
  ${breakpoint('xs', 'md')`
    display: none;
  `};
`;

const FooterLinkExpanders = styled.ul`
  display: flex;
  flex-direction: column;
  ${m(0)};
  ${p(0)};
  list-style: none;

  ${breakpoint('md')`
    display: none;
  `};
`;

const LinkExpander = styled.button`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  border: none;
  color: var(--heading-text-color, ${colorLightest});
  background: none;
  padding: 0;
  ${px(4)};
  ${py(4)};
  line-height: ${copyLineHeight};

  &:focus {
    outline: ${focusOutlineWidth} ${focusOutlineStyle} ${focusOutlineColor};
    outline-offset: -${focusOutlineWidth};
  }
`;

const ExpanderItem = styled.li`
  display: block;
  position: relative;
  border-top: 1px solid var(--text-color, ${colorDarker});

  &:last-of-type {
    border-bottom: 1px solid var(--text-color, ${colorDarker});
  }
`;

const ExpanderLink = styled(Link).attrs({underline: false})`
  display: block;
  ${px(5)};
  ${py(4)};

  &&& {
    color: inherit;
  }

  &:focus {
    outline: ${focusOutlineWidth} ${focusOutlineStyle} ${focusOutlineColor};
    outline-offset: -${focusOutlineWidth};
  }
`;

const Chevron = styled(({expanded, ...otherProps}) => <ChevronDownSystemIcon {...otherProps} />)`
  ${({expanded}) => expanded && `transform: rotate(180deg);`};
  transition: transform 300ms cubic-bezier(0.55, 0.085, 0.68, 0.53);
`;

const ExpanderContent = styled.ul`
  ${m(0)};
  ${p(0)};
  list-style: none;
`;

const PhoneHeading = styled(FooterHeading)`
  display: inline-block;
  font-size: 1.5rem;
  color: var(--text-color, inherit);
`;

export const InternationalPhoneHeading = styled(FooterHeading)`
  font-size: ${copyFontSizeSmall};
`;

const ContactLink = styled(Link).attrs({underline: false})`
  display: inline-block;

  &&& {
    color: inherit;
    white-space: normal;
  }

  &:hover {
    text-decoration: underline;
  }
`;

const PhoneContactLink = styled(ContactLink)`
  &:focus {
    outline: ${focusOutlineWidth} ${focusOutlineStyle} ${focusOutlineColor};
    outline-offset: 0;
  }
`;

const FooterLink = styled(Link)`
  &&& {
    color: inherit;
    white-space: normal;
  }
`;

const FooterIcon = styled(({icon: Icon, ...rest}) => <Icon size="xs" {...rest} />)`
  vertical-align: sub;
`;

const ChevronRight = styled(ChevronRightSystemIcon)`
  vertical-align: sub;
  ${pl(2)};
`;

interface SocialIconProps {
  [key: string]: unknown;
}

const SocialIcon = styled.a<SocialIconProps>`
  ${mr(1)};
  display: inline-block;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-position: 8px 8px; /* center breaks html-sketchapp */
  background-size: 16px;
  background-repeat: no-repeat;

  border: 1px solid var(--text-color, ${colorLightest});
  background-color: var(--background-color, ${colorDarkest});

  &:focus {
    outline: ${focusOutlineWidth} ${focusOutlineStyle} ${focusOutlineColor};
    outline-offset: ${focusOutlineOffset};
  }
`;

interface SocialMediaLinkProps {
  label: string;
  [key: string]: unknown;
}

const SocialMediaLink: React.FC<SocialMediaLinkProps> = props => (
  <SocialIcon {...props}>
    <Hidden screen>
      <span>{props.label}</span>
    </Hidden>
  </SocialIcon>
);

const IconStateStyles = () => css`
  background-size: 16px 16px;
  background-position: center;
  &:hover,
  &:focus {
    background-color: var(--icon-hover-color, ${colorBlack});
  }
`;

const FacebookIcon = styled(SocialMediaLink)`
  && {
    background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2NCIgaGVpZ2h0PSI2NCI+PHBhdGggZmlsbD0iI0ZGRiIgZD0iTTM3LjEgNjRWMzQuOGg5LjhsMS41LTExLjRIMzcuMXYtNy4zYzAtMy4zLjktNS41IDUuNi01LjVoNlYuNEM0Ny43LjMgNDQuMiAwIDQwIDBjLTguNyAwLTE0LjYgNS4zLTE0LjYgMTV2OC40aC05Ljh2MTEuNGg5LjhWNjRoMTEuN3oiLz48L3N2Zz4=');
    ${IconStateStyles};
  }
`;

const TwitterIcon = styled(SocialMediaLink)`
  && {
    background-image: url('data:image/svg+xml;base64,PHN2ZyB2ZXJzaW9uPSIxLjEiIGlkPSJMYXllcl8xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB4PSIwcHgiIHk9IjBweCIKCSB3aWR0aD0iNjRweCIgaGVpZ2h0PSI2NHB4IiB2aWV3Qm94PSIwIDAgNjQgNjQiIGVuYWJsZS1iYWNrZ3JvdW5kPSJuZXcgMCAwIDY0IDY0IiB4bWw6c3BhY2U9InByZXNlcnZlIj4KPHBhdGggZmlsbD0iI0ZGRkZGRiIgZD0iTTY0LDEyLjJjLTIuNCwxLTQuOSwxLjgtNy41LDIuMWMyLjctMS42LDQuOC00LjIsNS44LTcuM0M1OS44LDguNSw1Nyw5LjYsNTQsMTAuMkM1MS41LDcuNiw0OC4xLDYsNDQuMyw2CglDMzcsNiwzMS4yLDExLjksMzEuMiwxOS4xYzAsMSwwLjEsMiwwLjMsM0MyMC42LDIxLjYsMTEsMTYuNCw0LjUsOC40Yy0xLjEsMS45LTEuOCw0LjItMS44LDYuNmMwLDQuNiwyLjMsOC42LDUuOCwxMC45CgljLTIuMi0wLjEtNC4yLTAuNy01LjktMS42YzAsMC4xLDAsMC4xLDAsMC4yYzAsNi40LDQuNSwxMS43LDEwLjUsMTIuOWMtMS4xLDAuMy0yLjMsMC41LTMuNSwwLjVjLTAuOCwwLTEuNy0wLjEtMi41LTAuMgoJYzEuNyw1LjIsNi41LDksMTIuMyw5LjFDMTUsNTAuMiw5LjMsNTIuMywzLjIsNTIuM2MtMS4xLDAtMi4xLTAuMS0zLjEtMC4yQzUuOCw1NS45LDEyLjgsNTgsMjAuMiw1OGMyNC4yLDAsMzcuNC0yMCwzNy40LTM3LjQKCWMwLTAuNiwwLTEuMSwwLTEuN0M2MC4xLDE3LjEsNjIuMywxNC44LDY0LDEyLjJ6Ii8+Cjwvc3ZnPg==');
    ${IconStateStyles};
  }
`;

const YoutubeIcon = styled(SocialMediaLink)`
  && {
    background-image: url('data:image/svg+xml;base64,PHN2ZyB2ZXJzaW9uPSIxLjEiIGlkPSJMYXllcl8xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB4PSIwcHgiIHk9IjBweCIKCSB3aWR0aD0iNjRweCIgaGVpZ2h0PSI2NHB4IiB2aWV3Qm94PSIwIDAgNjQgNjQiIGVuYWJsZS1iYWNrZ3JvdW5kPSJuZXcgMCAwIDY0IDY0IiB4bWw6c3BhY2U9InByZXNlcnZlIj4KPGc+Cgk8cGF0aCBmaWxsPSIjRkZGRkZGIiBkPSJNNjMuNCw0NC44YzAsMC0wLjYsNC40LTIuNSw2LjRjLTIuNCwyLjUtNS4yLDIuNi02LjQsMi43Yy05LDAuNi0yMi40LDAuNy0yMi40LDAuN1MxNS41LDU0LjQsMTAuMyw1NAoJCWMtMS40LTAuMy00LjYtMC4yLTcuMS0yLjdjLTEuOS0xLjktMi41LTYuNC0yLjUtNi40UzAsMzkuNiwwLDM0LjR2LTQuOWMwLTUuMiwwLjYtMTAuNCwwLjYtMTAuNHMwLjYtNC40LDIuNS02LjQKCQljMi40LTIuNSw1LjItMi42LDYuNC0yLjdDMTguNiw5LjUsMzIsOS41LDMyLDkuNWwwLDBjMCwwLDEzLjQsMCwyMi40LDAuNmMxLjMsMC4xLDQsMC4yLDYuNCwyLjdjMS45LDEuOSwyLjUsNi40LDIuNSw2LjQKCQlTNjQsMjQuNCw2NCwyOS42djQuOUM2NCwzOS42LDYzLjQsNDQuOCw2My40LDQ0Ljh6IE0yNS40LDIyLjN2MThsMTcuMi05TDI1LjQsMjIuM3oiLz4KPC9nPgo8L3N2Zz4=');
    ${IconStateStyles};
  }
`;

const InstagramIcon = styled(SocialMediaLink)`
  && {
    background-image: url('data:image/svg+xml;base64,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');
    ${IconStateStyles};
  }
`;

const LinkedInIcon = styled(SocialMediaLink)`
  && {
    background-image: url('data:image/svg+xml;base64,PHN2ZyBpZD0nTGF5ZXJfMScgeG1sbnM9J2h0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnJyB2aWV3Qm94PScwIDAgMjUuOCAyNS43Jz48c3R5bGU+LnN0MHtmaWxsOiNGRkZGRkY7fTwvc3R5bGU+PHBhdGggY2xhc3M9J3N0MCcgZD0nTTUuOCAyNS43SC40VjguNWg1LjN2MTcuMnpNMy4xIDYuMkMxLjQgNi4yIDAgNC44IDAgMy4xIDAgMS40IDEuNCAwIDMuMSAwYzEuNyAwIDMuMSAxLjQgMy4xIDMuMSAwIDEuNy0xLjQgMy4xLTMuMSAzLjF6bTIyLjcgMTkuNWgtNS4zdi04LjRjMC0yIDAtNC42LTIuOC00LjZzLTMuMiAyLjItMy4yIDQuNHY4LjVIOS4xVjguNWg1LjF2Mi4zaC4xQzE1IDkuNCAxNi44IDggMTkuNCA4YzUuNCAwIDYuNCAzLjYgNi40IDguMnY5LjV6Jy8+PC9zdmc+');
    ${IconStateStyles};
  }
`;

interface FooterExpanderProps {
  heading: string;
  children?: React.ReactNode;
}

const FooterExpander: React.FC<FooterExpanderProps> = ({heading, children}) => {
  const [isExpanded, setExpanded] = useState(false);

  return (
    <>
      <LinkExpander title={heading} aria-expanded={isExpanded} aria-haspopup="true" onClick={() => setExpanded(!isExpanded)}>
        <FooterHeading>{heading}</FooterHeading>
        <Chevron size="xs" expanded={isExpanded} />
      </LinkExpander>
      <Expander expanded={isExpanded} aria-label={heading}>
        <ExpanderContent>{children}</ExpanderContent>
      </Expander>
    </>
  );
};

FooterExpander.displayName = 'FooterExpander';
FooterExpander.propTypes = {
  heading: PropTypes.string.isRequired,
  children: PropTypes.node
};

interface SocialMediaIconLinkType {
  id: string;
  title: string;
  url: string;
  key: string;
}

const SocialMediaIconLink = ({link}: {link: SocialMediaIconLinkType}) => {
  const {title, id, url, key} = link;

  switch (key) {
    case 'Facebook':
      return <FacebookIcon key={id} component={`${key}Icon`} id={id} href={url} title={title} label={key} rel="nofollow" />;

    case 'Twitter':
      return <TwitterIcon key={id} component={`${key}Icon`} id={id} href={url} title={title} label={key} rel="nofollow" />;

    case 'Youtube':
      return <YoutubeIcon key={id} component={`${key}Icon`} id={id} href={url} title={title} label={key} rel="nofollow" />;

    case 'Instagram':
      return <InstagramIcon key={id} component={`${key}Icon`} id={id} href={url} title={title} label={key} rel="nofollow" />;

    case 'LinkedIn':
      return <LinkedInIcon key={id} component={`${key}Icon`} id={id} href={url} title={title} label={key} rel="nofollow" />;

    default:
      return <SocialIcon key={id} component={`${key}Icon`} id={id} href={url} title={title} label={key} rel="nofollow" />;
  }
};

interface PhoneHeadingsProps {
  countryOfOrigin: string;
  contact: ContactConfig;
  defaultOrCorporatePhoneNumber?: string;
}

const PhoneHeadings: React.FC<PhoneHeadingsProps> = ({countryOfOrigin, contact, defaultOrCorporatePhoneNumber}) => (
  <PhoneHeading>
    <Stack space={4}>
      {contact.phoneNumber && defaultOrCorporatePhoneNumber && (
        <Stack space={2}>
          {contact.overseasPhoneNumber && <InternationalPhoneHeading>From {countryOfOrigin}:</InternationalPhoneHeading>}
          <PhoneContactLink id="ga-footer-call-desktop" href={`tel:${defaultOrCorporatePhoneNumber.replace(/\s+/g, '')}`} aria-label={`Call from ${countryOfOrigin}`}>
            <Inline space={2}>
              <FooterIcon icon={PhoneSystemIcon} size="sm" />
              <strong>{defaultOrCorporatePhoneNumber}</strong>
            </Inline>
          </PhoneContactLink>
        </Stack>
      )}

      {contact.overseasPhoneNumber && (
        <Stack space={2}>
          <InternationalPhoneHeading>From overseas:</InternationalPhoneHeading>
          <PhoneContactLink id="ga-footer-call-overseas-desktop" href={`tel:${contact.overseasPhoneNumber.replace(/\s+/g, '')}`} aria-label="Call from overseas">
            <Inline space={2}>
              <FooterIcon icon={PhoneSystemIcon} size="sm" />
              <strong>{contact.overseasPhoneNumber}</strong>
            </Inline>
          </PhoneContactLink>
        </Stack>
      )}
    </Stack>
  </PhoneHeading>
);

// HTML footer maps to HTMLElement in Typescript
export interface FooterProps extends React.HTMLAttributes<HTMLElement> {
  config?: CustomFooterConfig;
  variation?: Variation;
  nzShielded?: React.ReactNode;
  isCorporate?: boolean;
  withAbsoluteLinks?: boolean;
  absoluteBaseUrl?: string;
}

export const Footer: React.FC<FooterProps> & {FooterWrapper: typeof FooterWrapper} & {
  FooterLinkColumns: typeof FooterLinkColumns;
} & {FooterLinkExpanders: typeof FooterLinkExpanders} & {
  AcknowledgmentOfCountry: typeof AcknowledgmentOfCountry;
} = props => {
  const {config: customConfig, variation = 'arhi', nzShielded, isCorporate, withAbsoluteLinks, absoluteBaseUrl, children, ...otherProps} = props;

  const config = customConfig ?? configByVariation[variation]({withAbsoluteLinks, absoluteBaseUrl});
  const {id, contact, countryOfOrigin = 'Australia', smallFooterConfig} = config;

  const defaultOrCorporatePhoneNumber = isCorporate && contact?.corporatePhoneNumber ? contact.corporatePhoneNumber : contact?.phoneNumber;

  const phoneNumberLink = contact?.contactUrl || '/contact-us';

  const shouldShowAcknowledgementOfCountry = countryOfOrigin === 'Australia';

  return (
    <FooterWrapper id={id} {...otherProps}>
      <FooterContents>
        <Container>
          <Columns space={{xs: 8, xl: 6}} collapseBelow="xl">
            <Column>
              <Stack space={{xs: 8, xl: 6}}>
                <Stack space={4}>
                  {contact?.heading && <FooterHeading>{contact.heading}</FooterHeading>}

                  {contact && !contact.contactLinks && !contact.openHours && contact.phoneNumber && (
                    <PhoneHeadings countryOfOrigin={countryOfOrigin} contact={contact} defaultOrCorporatePhoneNumber={defaultOrCorporatePhoneNumber} />
                  )}

                  {contact?.contactLinks && (
                    <Stack space={4}>
                      {defaultOrCorporatePhoneNumber && (
                        <Inline space={2}>
                          <FooterIcon icon={PhoneSystemIcon} />
                          <ContactLink id="ga-footer-contact-us-desktop" href={phoneNumberLink}>
                            {/* 
                              FIXME: hiding the phone number for nib is urgent so running with this for now.
                              We don't want to be conditionally rendering based on a variation.
                              We would rather a stricter schema for the config with less variation between variations, and use that config to power any conditional rendering logic.
                            */}
                            {variation === 'providers' ? (
                              <span>
                                Contact us on <strong>{defaultOrCorporatePhoneNumber}</strong>
                              </span>
                            ) : (
                              <>
                                <span>Contact us</span>
                                <ChevronRight size="xs" />
                              </>
                            )}
                          </ContactLink>
                        </Inline>
                      )}

                      {contact.contactLinks.map((link, i) => {
                        const Icon = link.icon;
                        return (
                          <Inline space={2} key={`footer-link-${i}-${link.id}-desktop`}>
                            {Icon && <Icon size="xs" />}
                            <ContactLink id={`${link.id}-desktop`} href={link.url} target={link.targetBlank ? '_blank' : ''}>
                              <span>{link.title}</span>
                              <ChevronRight size="xs" />
                            </ContactLink>
                          </Inline>
                        );
                      })}
                    </Stack>
                  )}

                  {contact?.openHours && (
                    <Stack space={4}>
                      {(contact.phoneNumber || contact.overseasPhoneNumber) && (
                        <PhoneHeadings countryOfOrigin={countryOfOrigin} contact={contact} defaultOrCorporatePhoneNumber={defaultOrCorporatePhoneNumber} />
                      )}
                      <Stack space={2}>
                        {contact.openHours.map((hours, i) => (
                          <div key={`footer-open-hours-${i}`}>{hours}</div>
                        ))}
                      </Stack>
                    </Stack>
                  )}

                  {contact?.email && contact?.address && (
                    <Stack space={4}>
                      {contact.email && (
                        <Columns space={2}>
                          <Column width="content">
                            <FooterIcon icon={EmailSystemIcon} />
                          </Column>
                          <Column>
                            <FooterHeading>Email</FooterHeading>
                            <ContactLink href={`mailto:${contact.email}`}>{contact.email}</ContactLink>
                          </Column>
                        </Columns>
                      )}

                      {contact.address && (
                        <Columns space={2}>
                          <Column width="content">
                            <FooterIcon icon={LocationSystemIcon} />
                          </Column>
                          <Column>
                            <FooterHeading>Address</FooterHeading>
                            <Stack space={2}>
                              <div>
                                {contact.address.mail.map((item, i) => (
                                  <div key={`footer-mail-address-${i}`}>{item}</div>
                                ))}
                              </div>
                              <div>
                                {contact.address.office.map((item, i) => (
                                  <div key={`footer-office-address-${i}`}>{item}</div>
                                ))}
                              </div>
                            </Stack>
                          </Column>
                        </Columns>
                      )}
                    </Stack>
                  )}

                  {contact && contact.contactUrl && (
                    <FooterLink id="ga-footer-contact-us-desktop" href={contact.contactUrl}>
                      Contact us
                    </FooterLink>
                  )}
                </Stack>

                {contact?.socialLinks && (
                  <Stack space={4}>
                    <FooterHeading>Stay connected</FooterHeading>
                    <Inline space={2}>
                      {contact.socialLinks.length > 0 && contact.socialLinks.map(link => <SocialMediaIconLink link={link} key={link.id} />)} {nzShielded}
                    </Inline>
                  </Stack>
                )}
              </Stack>
            </Column>

            {config.columns && (
              <Column width="content">
                <FooterLinkColumns space={{xs: 4, xl: 6}}>
                  {config.columns.map((column, i) => (
                    <Column key={`footer-links-column-${i}`}>
                      <Stack space={4}>
                        <FooterHeading>{column.title}</FooterHeading>
                        {column.children.map((link, i) => (
                          <FooterLink id={`${link.id}-desktop`} href={link.url} target={link.targetBlank ? '_blank' : ''} key={`footer-link-${i}-${link.id}-desktop`}>
                            {link.title}
                          </FooterLink>
                        ))}
                      </Stack>
                    </Column>
                  ))}
                </FooterLinkColumns>
              </Column>
            )}
          </Columns>
        </Container>
      </FooterContents>

      {config.columns !== undefined && (
        <FooterLinkExpanders>
          {config.columns.map(
            (column, i) =>
              column.title &&
              column.title !== ' ' && (
                <ExpanderItem key={`footer-link-expander-${i}-mobile`}>
                  <FooterExpander heading={column.title}>
                    {column.children.map((link, i) => (
                      <li key={`footer-link-${i}-${link.id}-mobile`}>
                        <ExpanderLink id={`${link.id}-mobile`} href={link.url} target={link.targetBlank ? '_blank' : ''}>
                          {link.title}
                        </ExpanderLink>
                      </li>
                    ))}
                    {config.columns![i + 1] &&
                      config.columns![i + 1].title === ' ' &&
                      config.columns![i + 1].children.map((link, i) => (
                        <li key={`footer-link-${i}-${link.id}-mobile`}>
                          <ExpanderLink id={`${link.id}-mobile`} href={link.url} target={link.targetBlank ? '_blank' : ''}>
                            {link.title}
                          </ExpanderLink>
                        </li>
                      ))}
                  </FooterExpander>
                </ExpanderItem>
              )
          )}
        </FooterLinkExpanders>
      )}

      {shouldShowAcknowledgementOfCountry ? <AcknowledgmentOfCountry /> : <Box id="footer-spacer" marginBottom={{xs: 8, md: 0}} />}

      <SmallFooter variation={variation} config={smallFooterConfig} isFooterAttachment withAbsoluteLinks={withAbsoluteLinks} absoluteBaseUrl={absoluteBaseUrl} />
    </FooterWrapper>
  );
};

Footer.displayName = 'Footer';
Footer.FooterWrapper = FooterWrapper;
Footer.FooterLinkColumns = FooterLinkColumns;
Footer.FooterLinkExpanders = FooterLinkExpanders;
Footer.AcknowledgmentOfCountry = AcknowledgmentOfCountry;

Footer.propTypes = {
  config: PropTypes.shape({
    id: PropTypes.string.isRequired
  }),
  variation: PropTypes.oneOf(validVariationValues),
  nzShielded: PropTypes.node,
  isCorporate: PropTypes.bool,
  withAbsoluteLinks: PropTypes.bool,
  absoluteBaseUrl: PropTypes.string
};

Footer.defaultProps = {
  variation: 'arhi',
  isCorporate: false,
  withAbsoluteLinks: false
};
